import { useState, useEffect, useMemo } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { format, differenceInCalendarDays, eachDayOfInterval, isBefore, startOfDay } from "date-fns";
import { toast } from "sonner";
import { useLang } from "../../context/LanguageContext";
import { useContent } from "../../context/ContentContext";
import { api, BUSINESS } from "../../lib/api";
import { Reveal, SectionHeading } from "../Reveal";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { CalendarCheck, Loader2 } from "lucide-react";

const fmt = (d) => format(d, "yyyy-MM-dd");

export const Availability = () => {
  const { t } = useLang();
  const { content } = useContent();
  const rate = content.pricing?.nightlyRate ?? BUSINESS.nightlyRate;
  const cleaning = content.pricing?.cleaningFee ?? BUSINESS.cleaningFee;
  const maxGuests = content.pricing?.maxGuests ?? BUSINESS.maxGuests;
  const [blocked, setBlocked] = useState([]);
  const [range, setRange] = useState(undefined);
  const [form, setForm] = useState({ name: "", email: "", phone: "", guests: "2", message: "" });
  const [promo, setPromo] = useState("");
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadAvailability = async () => {
    try {
      const { data } = await api.get("/availability");
      setBlocked(data.blocked || []);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => { loadAvailability(); }, []);

  const bookedDates = useMemo(
    () => blocked.map((s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); }),
    [blocked]
  );
  const blockedSet = useMemo(() => new Set(blocked), [blocked]);

  const nights = range?.from && range?.to ? differenceInCalendarDays(range.to, range.from) : 0;

  const hasConflict = useMemo(() => {
    if (!range?.from || !range?.to) return false;
    const days = eachDayOfInterval({ start: range.from, end: range.to });
    return days.slice(0, -1).some((d) => blockedSet.has(fmt(d)));
  }, [range, blockedSet]);

  // live quote (seasonal breakdown + discounts) from the backend
  useEffect(() => {
    if (!range?.from || !range?.to || nights < 1 || hasConflict) { setQuote(null); return; }
    let active = true;
    const id = setTimeout(async () => {
      try {
        const { data } = await api.post("/quote", {
          checkin: fmt(range.from), checkout: fmt(range.to), promo_code: promo.trim(),
        });
        if (active) setQuote(data);
      } catch (e) {
        if (active) setQuote(null);
      }
    }, 350);
    return () => { active = false; clearTimeout(id); };
  }, [range, nights, hasConflict, promo]);

  const breakdown = quote?.breakdown || (nights > 0 ? [{ label: null, rate, nights, amount: nights * rate }] : []);
  const discount = quote?.discount || 0;
  const total = quote?.total ?? (nights > 0 ? nights * rate + cleaning : 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!range?.from || !range?.to || nights < 1) { toast.error(t("booking.errDates")); return; }
    if (hasConflict) { toast.error(t("booking.errConflict")); return; }
    if (!form.name || !form.email) { toast.error(t("booking.errForm")); return; }
    setSubmitting(true);
    try {
      await api.post("/booking-requests", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        guests: Number(form.guests),
        checkin: fmt(range.from),
        checkout: fmt(range.to),
        message: form.message,
      });
      toast.success(t("booking.successTitle"), { description: t("booking.success") });
      setForm({ name: "", email: "", phone: "", guests: "2", message: "" });
      setRange(undefined);
    } catch (err) {
      toast.error(t("booking.errForm"));
    } finally {
      setSubmitting(false);
    }
  };

  const payNow = async () => {
    if (!range?.from || !range?.to || nights < 1) { toast.error(t("booking.errDates")); return; }
    if (hasConflict) { toast.error(t("booking.errConflict")); return; }
    if (!form.name || !form.email) { toast.error(t("booking.errForm")); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        name: form.name, email: form.email, phone: form.phone, guests: Number(form.guests),
        checkin: fmt(range.from), checkout: fmt(range.to), message: form.message,
        promo_code: promo.trim(),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.status === 409 ? t("booking.errConflict") : t("booking.errForm"));
      setSubmitting(false);
    }
  };

  const today = startOfDay(new Date());

  return (
    <section id="booking" className="scroll-mt-20 bg-[#FAF8F5] px-4 sm:px-8 py-20 lg:py-28">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <SectionHeading eyebrow={t("booking.eyebrow")} title={t("booking.title")} subtitle={t("booking.subtitle")} />
        </Reveal>

        <div className="mt-10 grid gap-8 lg:grid-cols-2">
          {/* Calendar */}
          <Reveal>
            <div className="rounded-3xl border border-stone-200/80 bg-white p-5 sm:p-7" data-testid="booking-calendar">
              <DayPicker
                mode="range"
                selected={range}
                onSelect={setRange}
                numberOfMonths={1}
                disabled={[{ before: today }, ...bookedDates]}
                modifiers={{ booked: bookedDates }}
                modifiersClassNames={{ booked: "rdp-day_booked" }}
                styles={{ caption: { color: "#1C1917" } }}
              />
              <div className="mt-4 flex flex-wrap gap-4 border-t border-stone-100 pt-4 text-xs">
                <span className="flex items-center gap-2"><i className="h-3 w-3 rounded-sm" style={{ background: "rgba(95,107,82,0.25)" }} />{t("booking.legendAvailable")}</span>
                <span className="flex items-center gap-2"><i className="h-3 w-3 rounded-sm" style={{ background: "rgba(200,90,50,0.3)" }} />{t("booking.legendBooked")}</span>
                <span className="flex items-center gap-2"><i className="h-3 w-3 rounded-sm bg-[#C85A32]" />{t("booking.legendSelected")}</span>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-stone-50 p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-stone-500">{t("booking.checkin")}</p>
                  <p className="mt-1 font-semibold text-stone-800" data-testid="booking-checkin-date">
                    {range?.from ? format(range.from, "dd / MM / yyyy") : "—"}
                  </p>
                </div>
                <div className="rounded-xl bg-stone-50 p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-stone-500">{t("booking.checkout")}</p>
                  <p className="mt-1 font-semibold text-stone-800" data-testid="booking-checkout-date">
                    {range?.to ? format(range.to, "dd / MM / yyyy") : "—"}
                  </p>
                </div>
              </div>

              {nights > 0 && !hasConflict && (
                <div className="mt-4 rounded-xl bg-[#5F6B52]/10 p-4" data-testid="booking-estimate">
                  {breakdown.map((b, i) => (
                    <div key={i} className="flex items-center justify-between text-sm text-stone-600" data-testid={`estimate-row-${i}`}>
                      <span>
                        {b.label ? <span className="font-medium text-stone-700">{b.label} · </span> : null}
                        €{b.rate} × {b.nights} {b.nights === 1 ? t("booking.night") : t("booking.nights")}
                      </span>
                      <span>€{b.amount}</span>
                    </div>
                  ))}
                  <div className="mt-1 flex items-center justify-between text-sm text-stone-600">
                    <span>{t("pricing.cleaning")}</span><span>€{cleaning}</span>
                  </div>
                  {discount > 0 && (
                    <div className="mt-1 flex items-center justify-between text-sm font-medium text-emerald-700" data-testid="booking-discount">
                      <span>{quote?.discount_kind === "lastminute" ? t("booking.lastMinute") : `${t("booking.promo")} ${quote?.discount_label || ""}`}</span>
                      <span>−€{discount}</span>
                    </div>
                  )}
                  <div className="mt-2 flex items-center justify-between border-t border-stone-300/50 pt-2 font-semibold text-stone-900">
                    <span>{t("booking.estTotal")}</span><span className="text-lg">€{total}</span>
                  </div>
                </div>
              )}
              {hasConflict && (
                <p className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-700" data-testid="booking-conflict">{t("booking.errConflict")}</p>
              )}
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="rounded-3xl border border-stone-200/80 bg-white p-5 sm:p-7">
              <h3 className="flex items-center gap-2 font-serif text-2xl text-stone-900">
                <CalendarCheck className="h-6 w-6 text-[#C85A32]" /> {t("booking.formTitle")}
              </h3>
              <div className="mt-6 space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-stone-700">{t("booking.name")} *</label>
                  <Input data-testid="booking-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-stone-700">{t("booking.email")} *</label>
                    <Input data-testid="booking-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-stone-700">{t("booking.phone")}</label>
                    <Input data-testid="booking-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-stone-700">{t("booking.guests")}</label>
                  <Select value={form.guests} onValueChange={(v) => setForm({ ...form, guests: v })}>
                    <SelectTrigger data-testid="booking-guests-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: maxGuests }, (_, i) => i + 1).map((n) => (
                        <SelectItem key={n} value={String(n)} data-testid={`guests-option-${n}`}>
                          {n} {n === 1 ? t("common.guest") : t("common.guests")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-stone-700">{t("booking.messageLabel")}</label>
                  <Textarea data-testid="booking-message-textarea" rows={3} placeholder={t("booking.messagePlaceholder")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-stone-700">{t("booking.promoLabel")}</label>
                  <Input data-testid="booking-promo-input" value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder={t("booking.promoPlaceholder")} className="uppercase" />
                  {promo.trim() && quote?.promo_valid === false && (
                    <p className="mt-1 text-xs text-red-600" data-testid="promo-invalid">{t("booking.promoInvalid")}</p>
                  )}
                  {quote?.discount_kind === "code" && (
                    <p className="mt-1 text-xs font-medium text-emerald-600" data-testid="promo-applied">{t("booking.promoApplied")}</p>
                  )}
                </div>
                <Button data-testid="booking-paynow-button" type="button" onClick={payNow} disabled={submitting} className="w-full bg-[#1C1917] py-6 text-base hover:bg-[#C85A32]">
                  {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("booking.submitting")}</> : (
                    <>{t("booking.payNow")}{total > 0 ? ` · €${total}` : ""}</>
                  )}
                </Button>
                <p className="text-center text-xs text-stone-400">{t("booking.payNowNote")}</p>
                <div className="flex items-center gap-3 py-1"><span className="h-px flex-1 bg-stone-200" /><span className="text-xs text-stone-400">·</span><span className="h-px flex-1 bg-stone-200" /></div>
                <Button data-testid="booking-submit-button" type="submit" disabled={submitting} variant="outline" className="w-full py-6 text-base">
                  {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("booking.submitting")}</> : t("booking.submit")}
                </Button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
